import { Fragment, useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  Alert,
  Anchor,
  Badge,
  Button,
  Group,
  Loader,
  Pagination,
  SegmentedControl,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';

import { RegistryInboundAttachModal } from '@/components/Modals/RegistryInboundAttachModal';
import { RegistryInboundDismissModal } from '@/components/Modals/RegistryInboundDismissModal';
import {
  useRegistryInboundAttachDialog,
  type RegistryInboundAttachTarget,
} from '@/hooks/useRegistryInboundAttachDialog';
import { useRegistryInboundDismissDialog } from '@/hooks/useRegistryInboundDismissDialog';
import { useRegistryInboundMail } from '@/hooks/useRegistryInboundMail';
import {
  describeRegistryInboundReason,
  describeSuggestionWhy,
  isRegistryInboundOpen,
  type RegistryInboundRow,
  type RegistryInboundView,
} from '@/types/registry-documents';
import { CountryMentionBadge } from '../RegistryOrdersCard/CountryMentionBadge';
import { QuotedMessage } from '../RegistryOrdersCard/QuotedMessage';
import { formatRegistryMessageTime } from '../RegistryOrdersCard/RegistryOrderMessages';
import classes from './RegistryInboundMailSection.module.css';

export const INBOUND_PAGE_SIZE = 20;

// Module-level so the SegmentedControl gets the same array every render.
const VIEW_OPTIONS: { value: RegistryInboundView; label: string }[] = [
  { value: 'open', label: 'Open' },
  { value: 'all', label: 'All' },
];

/** The table's named columns. A last, unnamed one holds each row's
 * actions, and an opened excerpt spans them all. */
const COLUMNS = ['Received', 'From', 'Subject', 'Documents', 'Why', 'Suggested order'] as const;

const TABLE_MIN_WIDTH = 900;

const EMPTY_WORDS: Record<RegistryInboundView, string> = {
  open: 'Every email from the desk is on an order.',
  all: 'No email from the desk has needed attention.',
};

/**
 * The emails from the research desk that the delivery worker could put on
 * no order: a quote answered with the company name and no GTI reference,
 * a reference nobody recognises, a thread that spans two orders. Each is
 * attached to its order from here, or dismissed; until then it counts on
 * the Admin badge.
 *
 * The orders the API suggests are shown, never applied: same-name
 * companies exist across jurisdictions, and the choice is recorded so the
 * suggestions can be measured before any of it is automated.
 *
 * `onAttached` tells the surface an order gained a message, so the order
 * queue beside this list re-reads too.
 */
export function RegistryInboundMailSection({ onAttached }: Readonly<{ onAttached?: () => void }>) {
  const [view, setView] = useState<RegistryInboundView>('open');
  const [page, setPage] = useState(1);
  const [notice, setNotice] = useState<string | null>(null);
  const {
    page: result,
    isLoading,
    error,
    attach,
    dismiss,
  } = useRegistryInboundMail({ view, page, pageSize: INBOUND_PAGE_SIZE });

  const afterAttach = useCallback(
    (_row: RegistryInboundRow, target: RegistryInboundAttachTarget) => {
      const where = target.reportLabel
        ? `${target.reference} (${target.reportLabel})`
        : target.reference;
      setNotice(`Attached to ${where}. The message now shows on the order.`);
      onAttached?.();
    },
    [onAttached]
  );
  const attachDialog = useRegistryInboundAttachDialog(attach, afterAttach);
  const afterDismiss = useCallback(() => setNotice('Email dismissed.'), []);
  const dismissDialog = useRegistryInboundDismissDialog(dismiss, afterDismiss);

  const changeView = (value: string) => {
    setView(value as RegistryInboundView);
    setPage(1);
  };

  const empty = result.items.length === 0;
  const totalPages = Math.ceil(result.total / INBOUND_PAGE_SIZE);

  // The set shrinks as emails are attached (by this person or another):
  // step back to the last page that still exists.
  useEffect(() => {
    if (!isLoading && !error && page > 1 && page > totalPages) {
      setPage(Math.max(1, totalPages));
    }
  }, [isLoading, error, page, totalPages]);

  return (
    <Stack gap="sm" data-testid="registry-inbound-section">
      <Group justify="space-between" align="flex-end" wrap="wrap">
        <Stack gap={0}>
          <Title order={3}>Mail that reached no order</Title>
          <Text size="sm" c="dimmed">
            Emails from the desk without an order reference. Attach each to its order or dismiss it.
          </Text>
        </Stack>
        <SegmentedControl
          size="xs"
          aria-label="Mail shown"
          data={VIEW_OPTIONS}
          value={view}
          onChange={changeView}
          data-testid="registry-inbound-view"
        />
      </Group>

      {error && (
        <Alert color="red" title="Could not load the unmatched mail">
          {error.message}
        </Alert>
      )}

      {notice && (
        <Alert
          color="blue"
          withCloseButton
          onClose={() => setNotice(null)}
          data-testid="registry-inbound-notice"
        >
          {notice}
        </Alert>
      )}

      {isLoading && empty && <Loader size="sm" />}

      {!isLoading && !error && result.total === 0 && (
        <Text size="sm" c="dimmed" data-testid="registry-inbound-empty">
          {EMPTY_WORDS[view]}
        </Text>
      )}

      {!empty && (
        <InboundTable
          rows={result.items}
          onAttach={attachDialog.open}
          onDismiss={dismissDialog.open}
        />
      )}

      {totalPages > 1 && (
        <Group justify="space-between">
          <Text size="sm" c="dimmed">
            Showing {(page - 1) * INBOUND_PAGE_SIZE + 1} to{' '}
            {Math.min(page * INBOUND_PAGE_SIZE, result.total)} of {result.total} emails
          </Text>
          <Pagination value={page} onChange={setPage} total={totalPages} size="sm" />
        </Group>
      )}

      {/* One dialog of each kind for the list rather than one per row: an
          attached or dismissed email leaves the open view, and its row
          with it. */}
      <RegistryInboundAttachModal {...attachDialog.modalProps} />
      <RegistryInboundDismissModal {...dismissDialog.modalProps} />
    </Stack>
  );
}

// ---------------------------------------------------------------------------

/** The table itself, as data in and markup out; exported for its story. */
export function InboundTable({
  rows,
  onAttach,
  onDismiss,
}: Readonly<{
  rows: RegistryInboundRow[];
  onAttach: (row: RegistryInboundRow) => void;
  onDismiss: (row: RegistryInboundRow) => void;
}>) {
  // Which emails are opened to their excerpt. Held here rather than in the
  // row: an opened excerpt is a second table row, a sibling of the one
  // that toggles it.
  const [openIds, setOpenIds] = useState<ReadonlySet<string>>(new Set());
  const toggle = (rowId: string) => {
    setOpenIds((current) => {
      const next = new Set(current);
      if (!next.delete(rowId)) {
        next.add(rowId);
      }
      return next;
    });
  };

  return (
    <Table.ScrollContainer minWidth={TABLE_MIN_WIDTH}>
      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            {COLUMNS.map((column) => (
              <Table.Th key={column}>{column}</Table.Th>
            ))}
            <Table.Th aria-label="Actions" />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((row) => (
            <Fragment key={row.id}>
              <InboundRow
                row={row}
                excerptOpen={openIds.has(row.id)}
                onToggleExcerpt={() => toggle(row.id)}
                onAttach={() => onAttach(row)}
                onDismiss={() => onDismiss(row)}
              />
              {openIds.has(row.id) && (
                <Table.Tr data-testid={`registry-inbound-excerpt-${row.id}`}>
                  <Table.Td colSpan={COLUMNS.length + 1}>
                    <QuotedMessage
                      message={{
                        sender: row.sender,
                        receivedAt: row.receivedAt,
                        text: row.excerpt ?? '',
                      }}
                      fallbackFacts="Sender and time not recorded"
                    />
                  </Table.Td>
                </Table.Tr>
              )}
            </Fragment>
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}

/** Whether a click on a row means "open it": selecting text to copy (a
 * sender, a subject) does not. */
function textSelected(): boolean {
  return (window.getSelection()?.toString() ?? '') !== '';
}

function InboundRow({
  row,
  excerptOpen,
  onToggleExcerpt,
  onAttach,
  onDismiss,
}: Readonly<{
  row: RegistryInboundRow;
  excerptOpen: boolean;
  onToggleExcerpt: () => void;
  onAttach: () => void;
  onDismiss: () => void;
}>) {
  const open = isRegistryInboundOpen(row);
  // Attached, the email's entry moved onto the order and the row no longer
  // carries its sender, subject, text or files: those cells say where the
  // details went, and there is no text here to open.
  const onTheOrder = row.resolution === 'attached' || row.orderId !== null;
  const suggestion = row.suggestions[0];

  return (
    <Table.Tr
      className={onTheOrder ? undefined : classes.row}
      // A click anywhere on the row is the mouse's way to the text; the
      // "Show text" button is the keyboard's and the screen reader's.
      onClick={() => {
        if (!onTheOrder && !textSelected()) {
          onToggleExcerpt();
        }
      }}
      data-testid={`registry-inbound-row-${row.id}`}
    >
      <Table.Td>
        <Text size="sm">{formatRegistryMessageTime(row.receivedAt) ?? 'Not recorded'}</Text>
      </Table.Td>
      <Table.Td>
        {row.sender ? (
          <Text size="sm" className={classes.wrap}>
            {row.sender}
          </Text>
        ) : (
          <Text size="sm" c="dimmed">
            {onTheOrder ? 'On the order' : 'Not recorded'}
          </Text>
        )}
      </Table.Td>
      <Table.Td>
        {row.subject === null ? (
          <Text size="sm" c="dimmed">
            {onTheOrder ? 'On the order' : 'Details not available yet'}
          </Text>
        ) : (
          <Text size="sm" className={classes.wrap}>
            {row.subject}
          </Text>
        )}
      </Table.Td>
      <Table.Td>
        <DocumentsCell row={row} onTheOrder={onTheOrder} />
      </Table.Td>
      <Table.Td>
        <Group gap={4} wrap="wrap">
          {row.reasons.map((reason) => (
            <Badge key={reason} size="xs" variant="light" color="gray">
              {describeRegistryInboundReason(reason)}
            </Badge>
          ))}
          {row.orderReference && (
            <Text size="xs" ff="monospace" c="dimmed">
              {row.orderReference}
            </Text>
          )}
          <CountryMentionBadge codes={row.countriesMentioned} />
        </Group>
      </Table.Td>
      <Table.Td>
        {suggestion ? (
          <Group gap={6} wrap="wrap">
            <Text size="sm" ff="monospace">
              {suggestion.reference}
            </Text>
            <Text size="sm">· {suggestion.companyName}</Text>
            <Badge size="xs" variant="light" color="blue">
              {describeSuggestionWhy(suggestion.why)}
            </Badge>
          </Group>
        ) : (
          <Text size="sm" c="dimmed">
            No suggestion
          </Text>
        )}
      </Table.Td>
      <Table.Td>
        <Group gap={4} wrap="nowrap" justify="flex-end">
          {open ? (
            <>
              <Button
                size="compact-xs"
                variant="light"
                // The row opens the excerpt; the buttons are not a way to it.
                onClick={(event) => {
                  event.stopPropagation();
                  onAttach();
                }}
                data-testid={`registry-inbound-attach-${row.id}`}
              >
                Attach
              </Button>
              <Button
                size="compact-xs"
                variant="subtle"
                color="red"
                onClick={(event) => {
                  event.stopPropagation();
                  onDismiss();
                }}
                data-testid={`registry-inbound-dismiss-${row.id}`}
              >
                Dismiss
              </Button>
            </>
          ) : (
            <ResolvedCell row={row} />
          )}
          {!onTheOrder && (
            <Button
              size="compact-xs"
              variant="subtle"
              color="gray"
              onClick={(event) => {
                event.stopPropagation();
                onToggleExcerpt();
              }}
              aria-expanded={excerptOpen}
              data-testid={`registry-inbound-excerpt-toggle-${row.id}`}
            >
              {excerptOpen ? 'Hide text' : 'Show text'}
            </Button>
          )}
        </Group>
      </Table.Td>
    </Table.Tr>
  );
}

/** How many files the email carried, with their names under the count as
 * text: a name read from the row, not from a hover. */
function DocumentsCell({
  row,
  onTheOrder,
}: Readonly<{ row: RegistryInboundRow; onTheOrder: boolean }>) {
  if (onTheOrder && row.documentsCount === 0 && row.documentNames.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        On the order
      </Text>
    );
  }
  return (
    <Stack gap={0}>
      <Text size="sm">{row.documentsCount}</Text>
      {row.documentNames.length > 0 && (
        <Text size="xs" c="dimmed" className={classes.wrap}>
          {row.documentNames.join(', ')}
        </Text>
      )}
    </Stack>
  );
}

/** What became of a resolved email: the order it went onto, or that it
 * was dismissed, with the note under it. The reference links to the
 * order's report when the row names it; a row from an API that predates
 * the report id keeps the reference as text, and the order is found in
 * the queue. */
function ResolvedCell({ row }: Readonly<{ row: RegistryInboundRow }>) {
  if (row.resolution === 'dismissed') {
    const note = row.resolutionNote?.trim();
    return (
      <Stack gap={0} align="flex-end">
        <Text size="sm" c="dimmed">
          Dismissed
        </Text>
        {note && (
          <Text size="xs" c="dimmed" className={classes.wrap}>
            {note}
          </Text>
        )}
      </Stack>
    );
  }
  const words = `Attached to ${row.orderReferenceAttached ?? 'an order'}`;
  if (row.orderReportId) {
    return (
      <Anchor
        component={Link}
        to={`/reports/${row.orderReportId}`}
        size="sm"
        // The row has a click handler of its own; the link is the only
        // action here.
        onClick={(event) => event.stopPropagation()}
        data-testid={`registry-inbound-attached-link-${row.id}`}
      >
        {words}
      </Anchor>
    );
  }
  return (
    <Text size="sm" c="dimmed">
      {words}
    </Text>
  );
}
